import { Injectable, signal, effect, computed } from '@angular/core';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { getFirebaseApp } from './firebase';

export type ThemePreference = 'light' | 'dark' | 'system';

function setCrewdleCookie(name: string, value: string): void {
  try {
    const date = new Date();
    date.setTime(date.getTime() + 365 * 24 * 60 * 60 * 1000);
    document.cookie = `crewdle_${name}=${encodeURIComponent(value)};expires=${date.toUTCString()};path=/;domain=.crewdle.ai;SameSite=Lax`;
  } catch {}
}

function getCrewdleCookie(name: string): string | null {
  try {
    const prefix = `crewdle_${name}=`;
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const trimmed = cookie.trim();
      if (trimmed.startsWith(prefix)) {
        return decodeURIComponent(trimmed.substring(prefix.length));
      }
    }
  } catch {}
  return null;
}

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly storageKey = 'theme';
  private readonly mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  private userDocUnsub: Unsubscribe | null = null;

  readonly preference = signal<ThemePreference>(this.loadPreference());

  readonly resolvedTheme = computed<'light' | 'dark'>(() => {
    const pref = this.preference();
    if (pref === 'system') {
      return this.mediaQuery.matches ? 'dark' : 'light';
    }
    return pref;
  });

  constructor() {
    // Apply theme whenever preference or system preference changes
    effect(() => {
      this.applyTheme(this.resolvedTheme());
    });

    // Listen for system preference changes
    this.mediaQuery.addEventListener('change', () => {
      if (this.preference() === 'system') {
        this.applyTheme(this.mediaQuery.matches ? 'dark' : 'light');
      }
    });

    // Sync theme from cookie when returning to this tab (cross-platform sync)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      const cookie = getCrewdleCookie('theme') as ThemePreference | null;
      if (cookie && ['light', 'dark', 'system'].includes(cookie) && cookie !== this.preference()) {
        this.preference.set(cookie);
        localStorage.setItem(this.storageKey, cookie);
      }
    });
  }

  setPreference(pref: ThemePreference): void {
    this.preference.set(pref);
    localStorage.setItem(this.storageKey, pref);
    setCrewdleCookie('theme', pref);
    this.syncToFirestore(pref);
  }

  /**
   * Apply a preference received from Firestore (e.g. agent-driven change).
   * Updates local caches but does not re-sync to Firestore.
   */
  private applyRemotePreference(pref: ThemePreference): void {
    if (pref === this.preference()) return;
    this.preference.set(pref);
    localStorage.setItem(this.storageKey, pref);
    setCrewdleCookie('theme', pref);
  }

  /**
   * Subscribe to the user's doc and react to remote theme changes. Supports
   * cross-tab/cross-device live sync and delivers agent-driven mutations.
   */
  startUserSync(uid: string): void {
    this.stopUserSync();
    const db = getFirestore(getFirebaseApp());
    this.userDocUnsub = onSnapshot(doc(db, 'users', uid), (snap) => {
      if (!snap.exists()) return;
      const remote = snap.data()['theme'];
      if (remote && ['light', 'dark', 'system'].includes(remote)) {
        this.applyRemotePreference(remote as ThemePreference);
      }
    });
  }

  stopUserSync(): void {
    if (this.userDocUnsub) {
      this.userDocUnsub();
      this.userDocUnsub = null;
    }
  }

  cycle(): void {
    const order: ThemePreference[] = ['light', 'dark', 'system'];
    const current = order.indexOf(this.preference());
    this.setPreference(order[(current + 1) % order.length]);
  }

  async loadUserTheme(): Promise<void> {
    try {
      const auth = getAuth(getFirebaseApp());
      const uid = auth.currentUser?.uid;
      if (!uid) return;

      const localTheme = localStorage.getItem(this.storageKey);
      const db = getFirestore(getFirebaseApp());
      const snapshot = await getDoc(doc(db, 'users', uid));
      const firestoreTheme = snapshot.exists() ? snapshot.data()['theme'] : null;

      if (localTheme && ['light', 'dark', 'system'].includes(localTheme)) {
        if (firestoreTheme !== localTheme) {
          await setDoc(doc(db, 'users', uid), { theme: localTheme }, { merge: true });
        }
        setCrewdleCookie('theme', localTheme);
      } else if (firestoreTheme && ['light', 'dark', 'system'].includes(firestoreTheme)) {
        this.preference.set(firestoreTheme as ThemePreference);
        localStorage.setItem(this.storageKey, firestoreTheme);
        setCrewdleCookie('theme', firestoreTheme);
      }
    } catch {}
  }

  private loadPreference(): ThemePreference {
    const saved = localStorage.getItem(this.storageKey) as ThemePreference | null;
    if (saved && ['light', 'dark', 'system'].includes(saved)) return saved;

    const cookie = getCrewdleCookie('theme') as ThemePreference | null;
    if (cookie && ['light', 'dark', 'system'].includes(cookie)) return cookie;

    return 'system';
  }

  private applyTheme(theme: 'light' | 'dark'): void {
    const html = document.documentElement;
    if (theme === 'dark') {
      html.classList.add('dark');
    } else {
      html.classList.remove('dark');
    }
  }

  private async syncToFirestore(pref: string): Promise<void> {
    try {
      const auth = getAuth(getFirebaseApp());
      const uid = auth.currentUser?.uid;
      if (!uid) return;

      const db = getFirestore(getFirebaseApp());
      await setDoc(doc(db, 'users', uid), { theme: pref }, { merge: true });
    } catch {}
  }
}
